import { useState } from "react";
import { Breadcrumb } from "../../../types";
import { offsetLabel } from "../format";
import { LevelBadge } from "./LevelBadge";

// The lead-up to a capture, oldest first, each crumb offset from the capture
// moment. A "capture" crumb (an earlier capture in the same flow) is
// highlighted and links to that capture.
export const Timeline = ({ crumbs, originIso, truncatedNote }: { crumbs: Breadcrumb[]; originIso: string; truncatedNote?: string }) => {
  const [open, setOpen] = useState<Set<number>>(new Set());
  if (!crumbs.length) return <p className="text-sm text-neutral-500">No breadcrumbs.</p>;
  return (
    <ol className="relative border-l border-neutral-200 dark:border-neutral-800 ml-2">
      {crumbs.map((c, i) => {
        const isCapture = c.category === "capture";
        const captureId = isCapture ? (c.data?.captureId as string | undefined) : undefined;
        const hasData = c.data && Object.keys(c.data).length > 0;
        return (
          <li key={`${c.seq}-${c.ts}-${i}`} className="ml-4 py-1.5">
            <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ${isCapture ? "bg-red-500" : "bg-neutral-300 dark:bg-neutral-700"}`} />
            <div className="flex items-baseline gap-2 text-xs text-neutral-500">
              <span className="font-mono tabular-nums w-20 shrink-0" title={c.ts}>{offsetLabel(c.ts, originIso)}</span>
              {c.level && <LevelBadge level={c.level} />}
              {c.category && <span className="rounded bg-neutral-100 dark:bg-neutral-800 px-1">{c.category}</span>}
              <span className="text-neutral-400">#{c.seq}</span>
            </div>
            <div className={`text-sm ${isCapture ? "font-semibold" : ""}`}>
              {captureId ? <a className="hover:underline" href={`#/capture/${encodeURIComponent(captureId)}`}>{c.message}</a> : c.message}
            </div>
            {hasData && (
              <button className="text-xs text-neutral-500 hover:underline" onClick={() => setOpen((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; })}>
                {open.has(i) ? "hide data" : "show data"}
              </button>
            )}
            {hasData && open.has(i) && (
              <pre className="mt-1 text-xs bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded p-2 overflow-x-auto">{JSON.stringify(c.data, null, 2)}</pre>
            )}
          </li>
        );
      })}
      {truncatedNote && <li className="ml-4 py-1.5 text-xs text-amber-700 dark:text-amber-300">{truncatedNote}</li>}
    </ol>
  );
};
